/** @jsxImportSource @emotion/react */

import { useEffect, useMemo, useState } from "react";
import {
  css,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import BaseButton from "components/common/Button/Base";
import Table from "components/common/Table";
import AlertDialog, { AlertData } from "components/common/Dialog/Alert";
import PaymentNoticeDetailDialog from "../Detail";
import { MatchCandidateBusiness } from "../SelectBusiness";
import { BUSINESS_PAYMENT_NOTICE_LIST_COLS } from "../../column.data";
import {
  OTHER_TAX_TYPE_LABELS,
  OtherTaxType,
  PaymentNotice
} from "models/paymentNotice.model";
import {
  getBusinessPaymentNotices,
  getOtherTaxBusinessPaymentNotices
} from "pages/remotes";
import { PALETTE } from "styles/base/variables";
import { convertRegNumber } from "utils/converter";

interface BusinessDetailDialogProps {
  isOpen: boolean;
  businessId: string | null;
  organizationId: string | null;
  period: string;
  // 지정하면 부가세/종소세/법인세 납부서(§2.11)만 조회한다. 이때 period는 납부연월(변환 없음).
  taxType?: OtherTaxType;
  // 매칭 변경 팝업에서 후보로 보여줄 사업자 목록 (현재 매칭된 사업자는 제외하고 노출됨)
  businessList: MatchCandidateBusiness[];
  onClose: () => void;
  // 이 팝업에서 납부서를 삭제했을 때, 급여관리 메인 화면의 사업자별 현황 테이블을
  // 갱신할 수 있도록 알려준다.
  onNoticeDeleted?: () => void;
  // 이 팝업에서 납부서의 매칭 사업자를 변경했을 때도 마찬가지로 갱신을 알려준다.
  // 변경된 납부서 id를 함께 넘겨 상위에서 재중복 체크를 돌릴 수 있게 한다.
  onNoticeRematched?: (paymentNoticeId: string) => void;
}

function BusinessDetailDialog({
  isOpen,
  businessId,
  organizationId,
  period,
  taxType,
  businessList,
  onClose,
  onNoticeDeleted,
  onNoticeRematched,
}: BusinessDetailDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [regNumber, setRegNumber] = useState("");
  const [paymentNotices, setPaymentNotices] = useState<PaymentNotice[]>([]);
  const [selectedNotice, setSelectedNotice] = useState<PaymentNotice | null>(
    null
  );
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertDialogData, setAlertDialogData] = useState<AlertData>({
    message: "",
  });

  useEffect(() => {
    if (!isOpen || !businessId) return;

    setIsLoading(true);
    (taxType
      ? getOtherTaxBusinessPaymentNotices(businessId, period, taxType)
      : getBusinessPaymentNotices(businessId, period)
    )
      .then((res) => {
        setIsLoading(false);
        setBusinessName(res.data?.name || "");
        setRegNumber(res.data?.regNumber || "");
        setPaymentNotices(res.data?.paymentNotices || []);
      })
      .catch((err) => {
        setIsLoading(false);
        setAlertDialogData({
          message:
            err.response?.data?.message ||
            "납부서 목록을 불러오는 중 오류가 발생했습니다.",
          type: "error",
        });
        setIsAlertOpen(true);
      });
  }, [isOpen, businessId, period, taxType]);

  // 부가세/종소세/법인세 납부서는 period가 귀속연월이 아니라 납부연월이다 (§3).
  const periodLabel = taxType ? "납부연월" : "귀속기간";
  const columns = useMemo(
    () =>
      BUSINESS_PAYMENT_NOTICE_LIST_COLS.map((col) =>
        col.id === "period" ? { ...col, label: periodLabel } : col
      ),
    [periodLabel]
  );

  const onDeleted = (paymentNoticeId: string) => {
    setPaymentNotices((prev) =>
      prev.filter((notice) => notice.id !== paymentNoticeId)
    );
    onNoticeDeleted?.();
    setAlertDialogData({ message: "삭제되었습니다.", type: "success" });
    setIsAlertOpen(true);
  };

  // 매칭 사업자가 변경되면 이 납부서는 더 이상 현재 사업자 소속이 아니므로 목록에서 제거한다.
  const onRematched = (paymentNoticeId: string) => {
    setPaymentNotices((prev) =>
      prev.filter((notice) => notice.id !== paymentNoticeId)
    );
    onNoticeRematched?.(paymentNoticeId);
    setAlertDialogData({ message: "매칭 사업자가 변경되었습니다.", type: "success" });
    setIsAlertOpen(true);
  };

  const closeDialog = () => {
    onClose();
    setTimeout(() => {
      setBusinessName("");
      setRegNumber("");
      setPaymentNotices([]);
      setSelectedNotice(null);
    }, 500);
  };

  return (
    <>
      <Dialog open={isOpen} onClose={closeDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {businessName || "사업자"}{" "}
          {taxType ? `${OTHER_TAX_TYPE_LABELS[taxType]} ` : ""}납부서 목록
          {regNumber && (
            <span
              css={css`
                margin-left: 8px;
                font-size: 13px;
                font-weight: 400;
                color: ${PALETTE.textMid};
              `}
            >
              ({convertRegNumber(regNumber)})
            </span>
          )}
        </DialogTitle>
        <DialogContent>
          <Table
            isLoading={isLoading}
            columns={columns}
            dataList={paymentNotices}
            minWidth={900}
            minHeight={240}
            clickRow={setSelectedNotice}
          />
        </DialogContent>
        <div
          css={css`
            margin: 6px 24px;
            border-top: 1px solid ${PALETTE.border};
          `}
        />
        <DialogActions
          css={css`
            padding: 12px 24px 20px 24px;
          `}
        >
          <BaseButton
            variant="outlined"
            color={PALETTE.primary}
            onClick={closeDialog}
          >
            닫기
          </BaseButton>
        </DialogActions>
      </Dialog>

      <PaymentNoticeDetailDialog
        isOpen={Boolean(selectedNotice)}
        paymentNotice={selectedNotice}
        organizationId={organizationId}
        businessList={businessList}
        onClose={() => setSelectedNotice(null)}
        onDeleted={onDeleted}
        onRematched={onRematched}
        periodLabel={periodLabel}
      />

      <AlertDialog
        isOpen={isAlertOpen}
        setIsOpen={setIsAlertOpen}
        alertData={alertDialogData}
      />
    </>
  );
}

export default BusinessDetailDialog;
